import React, { useState, useEffect } from "react";
import { Button, Container, Card, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Layout from "../../../../layout/default"; // Adjust your layout import accordingly
import { FaMapMarkerAlt, FaPhoneAlt, FaCheckCircle, FaBuilding, FaCity, FaAddressCard, FaMapPin, FaGlobeAsia, FaMapSigns, FaLocationArrow } from 'react-icons/fa';
import { FaSave } from "react-icons/fa";
import { FaTimesCircle } from "react-icons/fa";

const EditAddress = () => {
  const { id } = useParams();  // Get address ID from the URL params
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    address: "",
    phone_number: "",
    pin_code: "",
    state_id: "",
    district_id: "",
    city_id: "",
    zone_id: "",
  });

  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityHasZones, setCityHasZones] = useState(false); // New state to track if city has zones

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Fetch the address data when the component loads
  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          navigate("/shop/auths/login", { replace: true });
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_HOST}api/address/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const { address, phone_number, pin_code, state_id, district_id, city_id, zone_id } = response.data.data[0];
        setFormData({ address, pin_code, phone_number, state_id, district_id, city_id, zone_id });
      } catch (error) {
        console.error("Error fetching address details:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load address details.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAddress();
  }, [id, navigate]);

  // Fetch states when component mounts
  useEffect(() => {
    const fetchStates = async () => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      try {
        const response = await axios.get(`${process.env.REACT_APP_HOST}api/states`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setStates(response.data);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, []);

  // Fetch districts when state_id changes
  useEffect(() => {
    const fetchDistricts = async (state_id) => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken || !state_id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_HOST}api/state_districts?state_id=${state_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        setDistricts(response.data.data);
      } catch (error) {
        console.error("Error fetching districts:", error);
      }
    };

    if (formData.state_id) {
      fetchDistricts(formData.state_id);
    } else {
      setDistricts([]); // Reset districts if no state is selected
    }
  }, [formData.state_id]); // Dependency on formData.state_id

  // Fetch cities when district_id changes
  useEffect(() => {
    const fetchCities = async (district_id) => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken || !district_id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_HOST}api/district_cities?district_id=${district_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        setCities(response.data.data);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    if (formData.district_id) {
      fetchCities(formData.district_id);
    } else {
      setCities([]); // Reset cities if no district is selected
    }
  }, [formData.district_id]); // Dependency on formData.district_id

  // Fetch zones when city_id changes
  useEffect(() => {
    const fetchZones = async (city_id) => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken || !city_id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_HOST}api/city_zones?city_id=${city_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.data && response.data.data.length > 0) {
          setZones(response.data.data);
          setCityHasZones(true);
        } else {
          setZones([]);
          setCityHasZones(false);
          setFormData(prev => ({ ...prev, zone_id: "" })); // Reset zone_id if city has no zones
        }
      } catch (error) {
        console.error("Error fetching zones:", error);
        setCityHasZones(false);
      }
    };

    if (formData.city_id) {
      fetchZones(formData.city_id);
    } else {
      setZones([]); // Reset zones if no city is selected
      setCityHasZones(false);
    }
  }, [formData.city_id]); // Dependency on formData.city_id

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number length (must be exactly 10 digits)
    if (!/^\d{10}$/.test(formData.phone_number)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Phone Number",
        text: "Phone number must be exactly 10 digits.",
      });
      return;
    }

    // Validate pin code length (must be exactly 6 digits)
    if (!/^\d{6}$/.test(formData.pin_code)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Pin Code",
        text: "Pin code must be exactly 6 digits.",
      });
      return;
    }

    try {
      const authToken = localStorage.getItem("authToken");
      const user_id = localStorage.getItem("user_id");

      if (!authToken || !user_id) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "User not authenticated.",
        });
        return;
      }

      // Prepare data to send (don't include zone_id if city has no zones)
      const dataToSend = cityHasZones ? formData : {
        ...formData,
        zone_id: "" // Explicitly set to empty if city has no zones
      };

      // Send PUT request to update the address
      await axios.put(
        `${process.env.REACT_APP_HOST}api/address/${id}`,
        dataToSend,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // Success message with the new Swal.fire configuration
      Swal.fire({
        imageUrl: "/shop/images/Edit.svg",
        imageWidth: '100%',
        imageHeight: 'auto',
        imageAlt: "Welcome",
        title: "Profile Updated Successfully!",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: {
          image: 'full-size-image'
        }
      }).then(() => {
        navigate("/shop/auths/user-profile-page");
      });

    } catch (error) {
      let errorMessage = "Failed to update address. Please try again.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.errors || errorMessage;
      }
      Swal.fire({ icon: "error", title: "Error", text: errorMessage });
    }
  };

  const clear = () => {
    navigate("/shop/auths/user-profile-page"); // Redirect to the profile page after update
  };

  return (
    <Layout title="Edit Address">
      <Container className="mt-4">
        <Row className="justify-content-md-center mt-4">
          <Col md={8} className="shadow p-4 rounded">
            <h4 className="text-center text-primary mb-4" onSubmit={handleSubmit}>
              <FaMapMarkerAlt className="me-2" />
              Update Address Details
            </h4>

            <Form onSubmit={handleSubmit}>
              {/* Shipping Address */}

              <Form.Group className="mb-3">
                <Form.Label><FaAddressCard className="me-2" />Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter your full address"
                  required
                />
              </Form.Group>

              <Row className="mb-3">
                <Form.Group as={Col} controlId="formPhoneNumber">
                  <Form.Label><FaPhoneAlt className="me-2" />Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit phone number"
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="formPincode">
                  <Form.Label><FaMapPin className="me-2" />Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pin_code"
                    value={formData.pin_code}
                    onChange={handleInputChange}
                    placeholder="Enter 6-digit pin code"
                    required
                  />
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label><FaGlobeAsia className="me-2" />State</Form.Label>
                    <Form.Select
                      name="state_id"
                      value={formData.state_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>{state.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label><FaMapSigns className="me-2" />District</Form.Label>
                    <Form.Select
                      name="district_id"
                      value={formData.district_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select District</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>{district.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label><FaCity className="me-2" />City</Form.Label>
                    <Form.Select
                      name="city_id"
                      value={formData.city_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>{city.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {cityHasZones && (
                  <Col>
                    <Form.Group>
                      <Form.Label><FaBuilding className="me-2" />Zone</Form.Label>
                      <Form.Select
                        name="zone_id"
                        value={formData.zone_id}
                        onChange={handleInputChange}
                        required={cityHasZones}
                      >
                        <option value="">Select Zone</option>
                        {zones.map((zone) => (
                          <option key={zone.id} value={zone.id}>{zone.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row className="mt-4">
                <Col className="d-flex justify-content-start">
                  <Button
                    variant="success"
                    type="submit"
                    className="px-4 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                    style={{ minHeight: '42px', fontSize: '1rem' }}
                  >
                    <FaCheckCircle className="me-2" />
                    Update
                  </Button>
                </Col>

                <Col className="d-flex justify-content-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={clear}
                    className="px-4 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                    style={{ minHeight: '42px', fontSize: '1rem' }}
                  >
                    <FaTimesCircle className="me-2" />
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Container>
      <br />
      <br />
    </Layout>
  );
};

export default EditAddress;